/**
 * @fileoverview Construye URLs para endpoints de API, sincronización y autenticación OAuth2
 * siguiendo los principios de configuración modular, prioridad de configuración,
 * detección automática de ambiente y compatibilidad CORS.
 * 
 * IMPORTANTE: Soporta los subdominios especializados conforme a la arquitectura de microservicios:
 * - api.nubelytics.com: Para API general y OAuth2 (RFC 6749)
 * - sync.nubelytics.com: Para el microservicio de sincronización
 * 
 * Además, implementa estrategias anti-CORS según el entorno:
 * - Detección de desarrollo vs producción
 * - Ajuste de protocolo para evitar mezcla HTTP/HTTPS
 * - Soporte para modo proxy cuando el backend no está bien configurado
 * - Almacenamiento de diagnóstico para depuración
 */
import appConfig from '../infrastructure/config/AppConfig';

/**
 * Determina el modo actual de la aplicación
 * @private
 * @returns {string} Modo ('development', 'staging', 'production')
 */
function getEnvironmentMode() {
  // Modo fijo: la app opera exclusivamente en entorno productivo
  return 'production';
}


/**
 * Verifica si hay problemas de CORS recientes
 * @private
 * @returns {boolean} True si se detectaron problemas de CORS
 */
function hasCorsIssues() {
  try {
    // Verificar si hay errores CORS registrados recientemente
    const lastCorsError = localStorage.getItem('nubelytics_last_cors_error');
    if (lastCorsError) {
      try {
        const errorData = JSON.parse(lastCorsError);
        const errorTime = new Date(errorData.timestamp || Date.now());
        const currentTime = new Date();
        
        // Considerar errores CORS en la última hora
        const oneHour = 60 * 60 * 1000;
        if ((currentTime - errorTime) < oneHour) {
          return true;
        }
      } catch (e) {
        // Error al parsear, ignorar
      }
    }
  } catch (e) {
    // Error al acceder a localStorage, ignorar
  }
  
  return false;
}

/**
 * Determina si debemos usar un subdominio específico según configuración o ambiente
 * @private
 * @param {string} subdomainType - Tipo de subdominio ('api' o 'sync')
 * @param {Object} [options] - Opciones adicionales
 * @param {boolean} [options.forceCorsCompatMode=false] - Forzar modo compatible con CORS
 * @return {boolean} True si debemos usar el subdominio especificado
 */
function shouldUseSubdomain(subdomainType, options = {}) {
  // Validar tipo de subdominio (principio de diseño por contrato)
  if (!['api', 'sync'].includes(subdomainType)) {
    console.warn(`Tipo de subdominio no soportado: ${subdomainType}`);  
    return false;
  }
  
  // Si se detectaron problemas de CORS y estamos en desarrollo, evitar subdominios
  if (hasCorsIssues() && getEnvironmentMode() === 'development') {
    console.warn(`[apiUrlBuilder] Modo compatibilidad CORS activo - evitando usar subdominio ${subdomainType}`);
    // Registrar para diagnóstico
    try {
      localStorage.setItem('nubelytics_cors_compat_mode', 'true');
      localStorage.setItem('nubelytics_subdomain_skipped', subdomainType);
    } catch (e) { /* Ignorar errores */ }
    
    return false;
  }
  
  // Si se especifica modo de compatibilidad CORS
  if (options.forceCorsCompatMode) {
    return false;
  }
  
  // 1. Prioridad máxima: Configuración explícita en __FIREBASE_CONFIG__
  if (window.__FIREBASE_CONFIG__) {
    // Cada subdominio tiene su propia configuración
    const configKey = `use${subdomainType.charAt(0).toUpperCase() + subdomainType.slice(1)}Subdomain`;
    if (window.__FIREBASE_CONFIG__[configKey] !== undefined) {
      return Boolean(window.__FIREBASE_CONFIG__[configKey]);
    }
  }
  
  // 2. Segunda prioridad: Configuración en AppConfig
  if (appConfig.apiConfig) {
    // Buscar configuración específica para este subdominio
    const configKey = `use${subdomainType}Subdomain`;
    if (appConfig.apiConfig[configKey] !== undefined) {
      return Boolean(appConfig.apiConfig[configKey]);
    }
    
    // Fallback a configuración genérica de subdominios
    if (appConfig.apiConfig.useSubdomains !== undefined) {
      return Boolean(appConfig.apiConfig.useSubdomains);
    }
  }
  
  // 3. Fallback: Detección automática basada en ambiente
  const mode = getEnvironmentMode();
  
  // En producción siempre usar subdominios si estamos en nubelytics.com
  if (mode === 'production' && (
      window.location.hostname === 'nubelytics.com' || 
      window.location.hostname.endsWith('.nubelytics.com') ||
      window.location.hostname.includes('web.app') ||
      window.location.hostname.includes('firebaseapp.com')
  )) {
    return true;
  }
  
  // En desarrollo, no usar subdominio por defecto para evitar problemas CORS
  return false;
}

/**
 * Determina si debemos usar subdominio api.nubelytics.com (compatibilidad)
 * @private
 * @param {Object} [options] - Opciones adicionales
 * @param {boolean} [options.forceCorsCompatMode=false] - Forzar modo compatible con CORS
 * @return {boolean} True si debemos usar subdominio api
 */
function shouldUseApiSubdomain(options = {}) {
  return shouldUseSubdomain('api', options);
}

/**
 * Determina si debemos usar subdominio sync.nubelytics.com
 * @private
 * @param {Object} [options] - Opciones adicionales
 * @param {boolean} [options.forceCorsCompatMode=false] - Forzar modo compatible con CORS
 * @return {boolean} True si debemos usar subdominio sync
 */
function shouldUseSyncSubdomain(options = {}) {
  return shouldUseSubdomain('sync', options);
}

/**
 * Función base para construir URLs con subdominio
 * @private
 * @param {string} subdomain - Nombre del subdominio ('api', 'sync', etc)
 * @param {string} path - Ruta relativa del endpoint
 * @param {Object} [options] - Opciones adicionales
 * @param {boolean} [options.forceCorsCompatMode=false] - Forzar modo compatible con CORS
 * @return {string} URL absoluta con el subdominio especificado
 */
function buildSubdomainUrl(subdomain, path, options = {}) {
  // Precondiciones (Principio de Diseño por Contrato)
  if (!subdomain) {
    console.error('buildSubdomainUrl requiere un subdominio válido');
    return appConfig.getApiUrl(path); // Fallback seguro
  }
  
  // Si se detectaron problemas de CORS, usar modo de compatibilidad
  if ((hasCorsIssues() || options.forceCorsCompatMode) && getEnvironmentMode() !== 'production') {
    // Registrar decisiones para depuración
    try {
      localStorage.setItem('nubelytics_using_cors_compat', 'true');
      localStorage.setItem('nubelytics_cors_subdomain', subdomain);
    } catch (e) { /* Ignorar errores */ }
    
    // En modo de compatibilidad CORS, delegamos a appConfig sin usar subdominio
    if (subdomain === 'api') {
      // Para API, usar URL base estándar
      const apiUrl = appConfig.getApiUrl('');
      const finalUrl = path ? `${apiUrl}${apiUrl.endsWith('/') ? '' : '/'}${path}` : apiUrl;
      
      console.debug(`[apiUrlBuilder] Modo CORS compat - API URL sin subdominio: ${finalUrl}`);
      return finalUrl;
    } else if (subdomain === 'sync') {
      // Para sync, integrar en la API base como /sync/...
      const apiUrl = appConfig.getApiUrl('');
      const syncPrefix = 'sync/';
      const syncPath = path ? 
        (path.startsWith(syncPrefix) ? path : `${syncPrefix}${path}`) : 
        syncPrefix;
      
      const finalUrl = `${apiUrl}${apiUrl.endsWith('/') ? '' : '/'}${syncPath}`;
      console.debug(`[apiUrlBuilder] Modo CORS compat - Sync URL sin subdominio: ${finalUrl}`);
      return finalUrl;
    }
  }
  
  // Normalizar path eliminando / iniciales
  const normalizedPath = path ? (path.startsWith('/') ? path.substring(1) : path) : '';
  
  // Determinar protocolo según ambiente y origen actual
  let protocol;
  if (getEnvironmentMode() === 'development' && window.location.hostname === 'localhost') {
    protocol = 'http:'; // Desarrollo local en HTTP
  } else {
    // Usar mismo protocolo que la página actual para evitar mezcla HTTP/HTTPS
    protocol = window.location.protocol || 'https:';
  }
  
  // Construir URL absoluta con subdominio
  const url = `${protocol}//${subdomain}.nubelytics.com/${normalizedPath}`;
  
  // Registrar para diagnóstico
  try {
    localStorage.setItem(`nubelytics_last_${subdomain}_url`, url);
  } catch (e) { /* Ignorar errores */ }
  
  return url;
}

/**
 * Construye URLs de API usando la configuración centralizada
 * con soporte para subdominio api.nubelytics.com cuando corresponda
 * e implementando estrategias de compatibilidad CORS
 * 
 * @param {string} path - Ruta relativa del endpoint
 * @param {Object} [options] - Opciones adicionales 
 * @param {boolean} [options.forceCorsCompatMode=false] - Forzar modo compatible con CORS
 * @return {string} URL completa de la API
 */
export function buildApiUrl(path, options = {}) {
  const { forceCorsCompatMode = false } = options;
  
  // Registrar diagnóstico para depuración
  try {
    localStorage.setItem('nubelytics_api_url_builder_path', path || '');
    localStorage.setItem('nubelytics_api_cors_compat', String(forceCorsCompatMode));
    localStorage.setItem('nubelytics_use_api_subdomain', 
      String(shouldUseApiSubdomain({ forceCorsCompatMode })));
  } catch (e) {/* Ignorar errores de localStorage */}
  
  // Si debemos usar subdominio api, construir URL absoluta
  // Cuando usemos el subdominio api.nubelytics.com debemos asegurarnos de que la ruta
  // contenga el prefijo "/api" para evitar rutas 404 en Firebase Hosting/Cloud Functions.
  // Normalizamos la ruta para añadirlo sólo si falta.
  if (shouldUseApiSubdomain({ forceCorsCompatMode })) {
    const normalized = path ? (path.startsWith('/') ? path.substring(1) : path) : '';
    const apiPath = !normalized
      ? 'api' // Sin ruta específica: usar solo el prefijo /api para que la baseURL sea /api
      : (normalized.startsWith('api/') || normalized === 'api')
        ? normalized // Ya contiene prefijo
        : `api/${normalized}`;
    return buildSubdomainUrl('api', apiPath, { forceCorsCompatMode });
  }
  
  // Si se detectó problema CORS y estamos en desarrollo, agregar parámetros
  // para ayudar al backend a responder correctamente
  if ((hasCorsIssues() || forceCorsCompatMode) && getEnvironmentMode() !== 'production') {
    const apiUrl = appConfig.getApiUrl(path);
    
    // Añadir parámetro para identificar el origen
    const separator = apiUrl.includes('?') ? '&' : '?';
    const enhancedUrl = `${apiUrl}${separator}origin=${encodeURIComponent(window.location.origin)}&_cors_fix=true`;
    
    // Registrar para diagnóstico
    try {
      localStorage.setItem('nubelytics_cors_param_added', 'true');
      localStorage.setItem('nubelytics_last_enhanced_url', enhancedUrl);
    } catch (e) {/* Ignorar errores */}
    
    console.debug(`[apiUrlBuilder] Modo compatibilidad CORS - URL con parámetros especiales: ${enhancedUrl}`);
    return enhancedUrl;
  }
  
  // Si no usamos subdominio api, delegar a appConfig (comportamiento original)
  return appConfig.getApiUrl(path);
}

/**
 * Construye URLs para endpoints de autenticación OAuth2
 * siguiendo RFC 6749 para garantizar compatibilidad con estándares
 * e implementando estrategias de compatibilidad CORS
 * 
 * @param {string} path - Ruta relativa del endpoint de autenticación
 * @param {Object} [options] - Opciones adicionales
 * @param {boolean} [options.forceCorsCompatMode=false] - Forzar modo compatible con CORS
 * @return {string} URL completa para autenticación
 */
export function buildAuthUrl(path, options = {}) {
  const { forceCorsCompatMode = false } = options;
  
  // Registrar para diagnóstico
  try {
    localStorage.setItem('nubelytics_auth_url_path', path || '');
    localStorage.setItem('nubelytics_auth_cors_compat', String(forceCorsCompatMode));
  } catch (e) {/* Ignorar errores */}
  
  // Para OAuth2, los endpoints de auth deben seguir el mismo patrón que API
  // ya que la autenticación es un servicio más en el subdominio api
  if (shouldUseApiSubdomain({ forceCorsCompatMode })) {
    // Normalizar path eliminando / iniciales
    const normalizedPath = path ? (path.startsWith('/') ? path.substring(1) : path) : '';
    const authPath = normalizedPath.startsWith('auth/') ? normalizedPath : `auth/${normalizedPath}`;
    
    // Usando buildSubdomainUrl para mantener la coherencia (DRY)
    return buildSubdomainUrl('api', authPath, { forceCorsCompatMode });
  }
  
  // Si se detectó problema CORS y estamos en desarrollo, agregar parámetros
  // para ayudar al backend a responder correctamente con los headers CORS adecuados
  if ((hasCorsIssues() || forceCorsCompatMode) && getEnvironmentMode() !== 'production') {
    const authUrl = appConfig.getAuthUrl(path);
    
    // Añadir parámetro para identificar el origen y solicitar headers CORS correctos
    const separator = authUrl.includes('?') ? '&' : '?';
    const enhancedUrl = `${authUrl}${separator}origin=${encodeURIComponent(window.location.origin)}&_cors_fix=true&_request_id=${Date.now()}`;
    
    console.debug(`[apiUrlBuilder] Modo compatibilidad CORS para Auth - URL mejorada: ${enhancedUrl}`);
    return enhancedUrl;
  }
  
  // Si no usamos subdominio, seguir usando la implementación existente
  return appConfig.getAuthUrl(path);
}

/**
 * Construye URLs para el microservicio de sincronización
 * siguiendo principios de arquitectura de microservicios
 * e implementando estrategias de compatibilidad CORS
 * 
 * @param {string} path - Ruta relativa del endpoint de sincronización
 * @param {Object} [options] - Opciones adicionales
 * @param {boolean} [options.useApiAsFallback=true] - Si debe usar api.nubelytics.com como fallback
 * @param {boolean} [options.forceCorsCompatMode=false] - Forzar modo compatible con CORS 
 * @return {string} URL completa para el servicio de sincronización
 */
export function buildSyncUrl(path, options = {}) {
  const { 
    useApiAsFallback = true,
    forceCorsCompatMode = false 
  } = options;
  
  // Registrar diagnóstico para depuración
  try {
    localStorage.setItem('nubelytics_sync_url_path', path || '');
    localStorage.setItem('nubelytics_sync_cors_compat', String(forceCorsCompatMode));
    localStorage.setItem('nubelytics_use_sync_subdomain', 
      String(shouldUseSyncSubdomain({ forceCorsCompatMode })));
  } catch (e) {/* Ignorar errores de localStorage */}
  
  // Normalizar path eliminando / iniciales
  const normalizedPath = path ? (path.startsWith('/') ? path.substring(1) : path) : '';
  
  // Si debemos usar subdominio sync.nubelytics.com, construir URL absoluta
  if (shouldUseSyncSubdomain({ forceCorsCompatMode })) {
    return buildSubdomainUrl('sync', normalizedPath, { forceCorsCompatMode });
  }
  
  // Si no usamos subdominio sync pero sí api, usar /sync en api.nubelytics.com como fallback
  if (useApiAsFallback && shouldUseApiSubdomain({ forceCorsCompatMode })) {
    const syncPath = normalizedPath ? `sync/${normalizedPath}` : 'sync';
    return buildSubdomainUrl('api', syncPath, { forceCorsCompatMode });
  }
  
  // Si se detectó problema CORS y estamos en desarrollo, agregar parámetros 
  // para ayudar al backend a responder correctamente
  if ((hasCorsIssues() || forceCorsCompatMode) && getEnvironmentMode() !== 'production') {
    const apiUrl = appConfig.getApiUrl('');
    const syncPath = normalizedPath ? `sync/${normalizedPath}` : 'sync';
    const baseUrl = `${apiUrl}${apiUrl.endsWith('/') ? '' : '/'}${syncPath}`;
    
    // Añadir parámetros para identificar origen y solicitar headers CORS correctos
    const separator = baseUrl.includes('?') ? '&' : '?';
    const enhancedUrl = `${baseUrl}${separator}origin=${encodeURIComponent(window.location.origin)}&_cors_fix=true&service=sync`;
    
    // Registrar para diagnóstico
    try {
      localStorage.setItem('nubelytics_sync_cors_url', enhancedUrl);
    } catch (e) {/* Ignorar errores */}
    
    console.debug(`[apiUrlBuilder] Modo compatibilidad CORS para Sync - URL mejorada: ${enhancedUrl}`);
    return enhancedUrl;
  }
  
  // Si no usamos ningún subdominio, usar la configuración centralizada
  const apiUrl = appConfig.getApiUrl('');
  const syncPath = normalizedPath ? `sync/${normalizedPath}` : 'sync';
  return `${apiUrl}${apiUrl.endsWith('/') ? '' : '/'}${syncPath}`;
}
